<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleClick"
      >
        Resubscribe
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Confirm payment method
  </DialogHeader>
  <DialogContent>
    <div>
      <div class="flex items-center mb-5">
        <Radio
          v-if="billingStore.billing?.paymentMethod"
          v-model="paymentMethodType"
          value="current"
          class="mr-7"
        >
          Current
        </Radio>
        <Radio v-model="paymentMethodType" value="new">
          New payment method
        </Radio>
      </div>
      <Message v-if="errorMessage" class="mb-5" type="error">
        {{ errorMessage }}
      </Message>
      <div v-show="isPaymentMethodTypeCurrent">
        <div class="mb-4">Please confirm this payment method</div>
        <div class="flex items-center">
          <div class="w-10 h-[26px] mr-2.5">
            <Visa />
          </div>
          <div>
            .... .... ....
            {{ billingStore.billing?.paymentMethod?.card?.last4 }}
          </div>
        </div>
      </div>
      <div v-show="isPaymentMethodTypeNew">
        <div id="card" />
      </div>
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      :loading="loading"
      primary
      @click="handleClick"
    >
      Resubscribe
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import Visa from '@/assets/svg/cards/Visa.svg'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import {
  BillingQueryDocument,
  PlatformType,
  useCreateSubscriptionMutation,
  type BillingPlan,
  type BillingQueryQuery,
} from '@/shared/graphql/legacy/generated'
import { createCardElement, getStripe } from '@/shared/services/stripe'
import { useBillingStore } from '@/features/billing/store'
import { Icon, UiButton, Radio, Message } from '@dacr/ui'
import type {
  PaymentMethodResult,
  Stripe,
  StripeCardElement,
} from '@stripe/stripe-js'
import { computed, onMounted, ref } from 'vue'

const { close } = useDialog()

const { mutate: createSubscription } = useCreateSubscriptionMutation()

const billingStore = useBillingStore()

const paymentMethodType = ref<'current' | 'new'>(
  billingStore.billing?.paymentMethod ? 'current' : 'new'
)
const loading = ref(false)
const errorMessage = ref('')

const stripe = ref<Stripe | null>(null)
const card = ref<StripeCardElement | null>(null)

const isPaymentMethodTypeCurrent = computed(
  () => paymentMethodType.value === 'current'
)
const isPaymentMethodTypeNew = computed(() => paymentMethodType.value === 'new')

onMounted(async () => {
  stripe.value = await getStripe()
  if (!stripe.value) return

  card.value = createCardElement(stripe.value)
  card.value.mount('#card')
})

const handleClick = async () => {
  if (!stripe.value || !card.value) return

  loading.value = true

  try {
    const productId = billingStore.billing?.plan.id
    if (!productId) {
      throw new Error('Could not find product id')
    }

    let paymentMethodId: string | undefined = undefined
    let result: PaymentMethodResult | undefined = undefined
    if (!isPaymentMethodTypeCurrent.value) {
      result = await stripe.value.createPaymentMethod({
        type: 'card',
        card: card.value,
      })

      if (result.error) {
        errorMessage.value = result.error.message || ''
        loading.value = false
        return
      }

      paymentMethodId = result.paymentMethod?.id
    }

    await createSubscription(
      {
        productId,
        platform: PlatformType.Web,
        ...(paymentMethodId && { paymentMethodId }),
      },
      {
        update(cache, { data }) {
          cache.updateQuery<BillingQueryQuery>(
            { query: BillingQueryDocument },
            (response) => {
              if (!response) return

              const cardData = result?.paymentMethod?.card
              return {
                ...response,
                billing: {
                  ...response.billing,
                  plan: data?.createSubscription.plan as BillingPlan,
                  upcomingBilling:
                    data?.createSubscription.plan.details.upcomingBilling,
                  ...(cardData && {
                    paymentMethod: {
                      __typename: 'PaymentMethod',
                      card: {
                        last4: cardData.last4,
                        type: cardData.brand,
                      },
                      requiresAction: null,
                    },
                  }),
                  billingAlert: null,
                },
              } as BillingQueryQuery
            }
          )
        },
      }
    )

    close()
  } catch (error) {
    console.error(error)

    if (error instanceof Error) {
      errorMessage.value = error.message
    }
  } finally {
    loading.value = false
  }
}
</script>

<style>
#card > .__PrivateStripeElement {
  width: 100%;
}
</style>
